import { useState } from 'react';
import { Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAiLayersStore, type AiLayer } from '@/lib/store/aiLayers';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const SUGGESTIONS = ['Wildfires', 'Weather', 'Roads', 'Wildlife', 'Power Lines', 'Construction'];

interface AiCandidate extends Omit<AiLayer, 'id' | 'visible'> {
  relevant: boolean;
}

// Mock AI search — returns plausible-looking layer candidates. Real impl proxies through backend.
async function mockAiSearch(query: string): Promise<AiCandidate[]> {
  await new Promise((r) => setTimeout(r, 800 + Math.random() * 600));
  const variants: Record<string, AiCandidate[]> = {
    wildfires: [
      { name: 'Alberta Wildfire Perimeter', source: 'Alberta Wildfire', url: 'https://wildfire.alberta.ca/perimeter.geojson', description: 'Active fire perimeters refreshed every 15 min.', format: 'GeoJSON', color: '#dc2626', relevant: true },
      { name: 'BC Hot Spots', source: 'NRCan', url: 'https://hot-spots.nrcan.gc.ca/wms', description: 'MODIS thermal anomalies.', format: 'WMS', color: '#f97316', relevant: true },
    ],
    weather: [
      { name: 'METAR Stations', source: 'NavCanada', url: 'https://plan.navcanada.ca/metar.geojson', description: 'Current METAR observations.', format: 'GeoJSON', color: '#2563eb', relevant: true },
      { name: 'Convective SIGMET', source: 'Environment Canada', url: 'https://weather.gc.ca/sigmet.geojson', description: 'Active convective sigmets.', format: 'GeoJSON', color: '#7c3aed', relevant: true },
    ],
    roads: [
      { name: 'OpenStreetMap Roads', source: 'OSM', url: 'https://overpass-api.de/...', description: 'Highway centerlines.', format: 'GeoJSON', color: '#0d7f6a', relevant: true },
    ],
  };
  const key = query.toLowerCase().split(/\s+/)[0] ?? 'roads';
  return variants[key] ?? [{ name: `${query} (generic)`, source: 'Generic', url: '#', description: `Generic layer for "${query}".`, format: 'GeoJSON', color: '#6b7280', relevant: false }];
}

export function AILayerSearchModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<AiCandidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const add = useAiLayersStore((s) => s.add);

  const run = async (query: string) => {
    setLoading(true);
    setResults(null);
    try {
      const r = await mockAiSearch(query);
      setResults(r);
    } catch {
      toast.error('Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>AI Layer Search</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 flex-wrap">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { setQ(s); run(s); }}
              className="px-2 h-7 rounded-full text-[0.692em] font-semibold border border-(--color-border) bg-(--color-surface) hover:border-(--color-green)"
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && q && run(q)}
            placeholder="Search for a data layer…"
            className="flex-1 h-9 px-3 rounded-md border border-(--color-border) bg-(--color-surface) text-[0.846em] outline-none focus:border-(--color-green)"
          />
          <Button onClick={() => q && run(q)} disabled={!q || loading}>
            <Search size={13} /> {loading ? 'Searching…' : 'Search'}
          </Button>
        </div>
        {loading && (
          <div className="space-y-1.5 mt-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        {results && results.length === 0 && <div className="text-[0.769em] text-(--color-text3)">No results.</div>}
        {results && results.length > 0 && (
          <div className="space-y-1.5 mt-2 max-h-[300px] overflow-y-auto">
            {results.map((r) => (
              <ResultCard key={r.name} c={r} onAdd={() => { add(r); toast.success(`Added "${r.name}"`); }} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultCard({ c, onAdd }: { c: AiCandidate; onAdd: () => void }) {
  const [added, setAdded] = useState(false);
  return (
    <article className="rounded-md border border-(--color-border) bg-(--color-surface) p-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full shrink-0" style={{ background: c.color }} />
            <span className="text-[0.846em] font-bold truncate">{c.name}</span>
            <span className="text-[0.615em] font-mono text-(--color-text3) uppercase">{c.format}</span>
          </div>
          <div className="text-[0.692em] text-(--color-text3) mt-0.5">{c.source}</div>
          <div className="text-[0.692em] text-(--color-text2) mt-1">{c.description}</div>
        </div>
        <Button size="xs" disabled={added} onClick={() => { onAdd(); setAdded(true); }}>
          {added ? '✓ Added' : 'Add'}
        </Button>
      </div>
    </article>
  );
}
